'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { useMemo, useState } from 'react';
import { ConfirmDialog, GenericDetailPage, Tabs, Spinner, Button } from '@repo/components';
import { Role, Type } from '@repo/shared/domain';
import { EventEmitter, guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { Trash2, Copy } from 'lucide-react';
import { useConfig } from 'src/hooks/useConfig';
import AccessTab from './Tabs/Access';
import GeneralTab from './Tabs/General';
import PermissionsTab from './Tabs/Permissions';
import PrivacyTab from './Tabs/Privacy';
import RequirementsTab from './Tabs/Requirements';
import ManagementTab from './Tabs/SiteManagement';
import EventGroupManagementTab, { TypesOnRoles } from './Tabs/EventGroupManagement';
import { getTaskTemplates } from 'src/api/admin/taskTemplates';
import { getAllRoles } from 'src/api/admin/roles';
import { getAllZones } from 'src/api/admin/zones';
import { getAllSites } from 'src/api/admin/sites';
import { getTypesForRole, getAllTypes } from 'src/api/admin/types';
export const Route = createFileRoute('/_admin/config/roles/$roleId')({
	component: RoleDetailPage,
});

const getRole = async (roleId: string): Promise<Role | undefined> => {
	try {
		const { body } = await request.get<undefined, Role>(`/api/roles/${roleId}`, { schema: undefined });
		return body;
	} catch {
		return undefined;
	}
};

function getDuplicateName(name: string): string {
	const copyPattern = /^(.+)\s+\(Copy(?:\s+(\d+))?\)$/;
	const match = name.match(copyPattern);

	if (match) {
		const baseName = match[1];
		const copyNumber = match[2] ? parseInt(match[2], 10) + 1 : 2;
		return `${baseName} (Copy ${copyNumber})`;
	}

	return `${name} (Copy)`;
}

function determineOpsForTypes(
	originalTypes: Type[],
	originalAssignableInEventTypes: Type[],
	typesOnRole: TypesOnRoles,
	parentRoleIds: string[] = [],
) {
	const originalTypeIds = originalTypes.map((type) => type.id);
	const newTypes = typesOnRole.eventTypes.concat(typesOnRole.groupTypes);
	const deletedTypes = originalTypeIds.filter((type) => !newTypes.includes(type));
	const addedTypes = newTypes.filter((type) => !originalTypeIds.includes(type));

	const originalAssignableInEventTypeIds = originalAssignableInEventTypes.map((type) => type.id);
	const newAssignableInEventTypes = typesOnRole.assignableInEventTypes;
	const deletedAssignableInEventTypes = originalAssignableInEventTypeIds.filter(
		(type) => !newAssignableInEventTypes.includes(type),
	);
	const addedAssignableInEventTypes = newAssignableInEventTypes.filter(
		(type) => !originalAssignableInEventTypeIds.includes(type),
	);

	return {
		hasChanged:
			deletedTypes.length > 0 ||
			addedTypes.length > 0 ||
			deletedAssignableInEventTypes.length > 0 ||
			addedAssignableInEventTypes.length > 0,
		deletedTypes,
		addedTypes,
		deletedAssignableInEventTypes,
		addedAssignableInEventTypes,
		parentRoleIds: addedAssignableInEventTypes.length > 0 ? parentRoleIds : [],
	};
}

function RoleDetailPage() {
	const { roleId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const [activeTab, setActiveTab] = useState(0);
	const [roleData, setRoleData] = useState<Role | null>(null);
	const [hasChanged, setChanged] = useState(false);
	const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isDuplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
	const [typesOnRoles, setTypesOnRoles] = useState<TypesOnRoles>({
		eventTypes: [],
		assignableInEventTypes: [],
		groupTypes: [],
	});
	const [initialized, setInitialized] = useState(false);

	useBreadcrumbLevel(0, 'Configuration', '/config/roles');
	useBreadcrumbLevel(1, 'Roles', '/config/roles');
	useBreadcrumbLevel(2, roleData?.name || 'Role Details');

	const { data: role, isLoading: roleLoading } = useQuery({
		queryKey: ['role', roleId],
		queryFn: () => getRole(roleId),
	});

	const { data: taskTemplatesResponse } = useQuery({
		queryKey: ['task-templates'],
		queryFn: () => getTaskTemplates('noPagination=true'),
	});
	const taskTemplates = taskTemplatesResponse?.data ?? [];

	const { data: zonesResponse } = useQuery({
		queryKey: ['zones-all'],
		queryFn: () => getAllZones(),
	});
	const zones = zonesResponse?.data ?? [];

	const { data: rolesResponse } = useQuery({
		queryKey: ['roles-all'],
		queryFn: getAllRoles,
	});
	const roles = rolesResponse?.data ?? [];

	const { data: sitesResponse } = useQuery({
		queryKey: ['sites-all'],
		queryFn: () => getAllSites(),
	});
	const sites = sitesResponse?.data ?? [];

	const { data: types = [], isLoading: typesLoading } = useQuery({
		queryKey: ['role-types', roleId],
		queryFn: () => getTypesForRole(roleId),
	});

	const { data: allTypes = [], isLoading: allTypesLoading } = useQuery({
		queryKey: ['all-types'],
		queryFn: getAllTypes,
	});

	const eventTypes = allTypes.filter((type) => type.model === 'event');
	const groupTypes = allTypes.filter((type) => type.model === 'group');

	// Wait for all three queries before initializing — types & allTypes arrive after role,
	// and initializing too early leaves assignableInEventTypes empty (and a save would wipe it).
	if (!initialized && !roleLoading && !typesLoading && !allTypesLoading && role) {
		setRoleData(role);
		setTypesOnRoles({
			eventTypes: types.filter((type) => type.model === 'event').map((type) => type.id),
			assignableInEventTypes: eventTypes
				.filter((type) => type.possibleRoleIds?.includes(roleId))
				.map((type) => type.id),
			groupTypes: types.filter((type) => type.model === 'group').map((type) => type.id),
		});
		setInitialized(true);
	}

	const handleChange = (data: Role) => {
		setChanged(true);
		setRoleData(data);
	};

	const handleDelete = async () => {
		await request.del(`/api/roles/${roleId}`);
		queryClient.invalidateQueries({ queryKey: ['roles'] });
		navigate({ to: '/config/roles' });
	};

	const handleDuplicate = async () => {
		if (!roleData) return;
		let isError;
		const duplicateRoleData = { ...roleData };
		duplicateRoleData.name = getDuplicateName(roleData.name || '');

		await request
			.post('/api/roles/duplicate', { roleData: duplicateRoleData, typeData: typesOnRoles })
			.then(({ body }: { body: Role }) => {
				queryClient.invalidateQueries({ queryKey: ['roles'] });
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Role duplicated',
				});
				window.open(`/config/roles/${body.id}`, '_blank');
			})
			.catch(() => {
				isError = true;
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to duplicate role. Did you enter all data correctly?',
				});
			});

		if (!isError) {
			setDuplicateDialogOpen(false);
		}
	};

	const saveChanges = async (passedData?: Role) => {
		if (!roleData) return;
		try {
			const originalAssignableInEventTypes = eventTypes.filter((type) => type.possibleRoleIds?.includes(roleId));
			const typeOps = determineOpsForTypes(types, originalAssignableInEventTypes, typesOnRoles, roleData.parentRoleIds || []);
			await request.put(`/api/roles/${roleId}`, {
				typeOps,
				...(passedData || roleData),
			});
			EventEmitter.emit('toast', { type: 'success', message: 'Role updated!' });
			setChanged(false);
			queryClient.invalidateQueries({ queryKey: ['role', roleId] });
			queryClient.invalidateQueries({ queryKey: ['all-types'] });
		} catch (err: any) {
			console.error(err);
			EventEmitter.emit('toast', {
				type: 'error',
				message: err.status === 405 ? err.message : 'Failed to update role.',
			});
		}
	};

	const onTypesChanged = (newTypes: TypesOnRoles) => {
		setTypesOnRoles(newTypes);
		setChanged(true);
	};

	const parentRoles = useMemo(() => {
		if (!roleData?.parentRoleIds?.length) return [];
		return roles.filter((r) => roleData.parentRoleIds!.includes(r.id));
	}, [roleData?.parentRoleIds, roles]);

	const handleAddAllAssignableInEventTypes = () => {
		const allIds = eventTypes.map((type) => type.id);
		setTypesOnRoles((prev) => ({
			...prev,
			assignableInEventTypes: allIds,
		}));
		setChanged(true);
	};

	const isLoading = roleLoading || typesLoading || allTypesLoading;
	if (isLoading || !roleData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = [
		{
			label: 'General',
			el: <GeneralTab data={roleData} onChange={handleChange} roles={roles} />,
		},
		{
			label: 'Requirements',
			el: (
				<RequirementsTab
					data={roleData}
					onChange={handleChange}
					taskTemplates={taskTemplates}
					role={role!}
					usesPlayit={featureFlags.USES_PLAYIT || false}
				/>
			),
		},
		{
			label: 'Site Management',
			el: <ManagementTab data={roleData} onChange={handleChange} sites={sites} />,
		},
		{
			label: 'Event & Company Management',
			el: (
				<EventGroupManagementTab
					values={typesOnRoles}
					eventTypes={eventTypes}
					assignableInEventTypes={eventTypes}
					groupTypes={groupTypes}
					parentRoles={parentRoles}
					onChange={onTypesChanged}
					onAddAllAssignableInEventTypes={handleAddAllAssignableInEventTypes}
				/>
			),
		},
		{
			label: 'Site Access',
			el: (
				<AccessTab
					data={roleData}
					onChange={handleChange}
					zones={zones}
					sites={sites}
					USES_RANDOM_CHECKS={featureFlags.USES_RANDOM_CHECKS || false}
					USES_EMERGENCY_MODULE={featureFlags.USES_EMERGENCY_MODULE || false}
				/>
			),
		},
		{
			label: 'Permissions',
			el: (
				<PermissionsTab
					data={roleData}
					onChange={handleChange}
					USES_PARTNER_PORTAL={featureFlags.USES_PARTNER_PORTAL || false}
				/>
			),
		},
		{
			label: 'Data & Privacy',
			el: <PrivacyTab data={roleData} onChange={handleChange} />,
		},
	];

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content className="bg-white">
					<Paper
						variant="outlined"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flexWrap: 'wrap',
							flexGrow: 1,
						}}
					>
						<Tabs
							value={activeTab}
							onChange={(i) => setActiveTab(i)}
							actions={
								<>
									<Button variant="outline-bg-white" onClick={() => setDuplicateDialogOpen(true)} size="sm">
										<Copy />
									</Button>
									<Button
										variant="outline-bg-white"
										disabled={!!(roleData as any).hasUsers}
										onClick={() => setDeleteDialogOpen(true)}
										size="sm"
									>
										<Trash2 />
									</Button>
									<Button
										disabled={!hasChanged}
										size="sm"
										variant="outline-bg-white"
										onClick={() => saveChanges(roleData)}
									>
										Save Changes
									</Button>
								</>
							}
						>
							<Tabs.TabList>
								{tabs.map((t, index) => (
									<Tabs.Tab key={t.label} value={index} label={t.label} />
								))}
							</Tabs.TabList>

							{tabs.map((tab, index) => (
								<Tabs.TabPanel selectedValue={activeTab} key={tab.label} value={index}>
									{roleData && tab.el}
								</Tabs.TabPanel>
							))}
						</Tabs>
					</Paper>
				</GenericDetailPage.Content>
			</GenericDetailPage>
			<ConfirmDialog
				isOpen={isDeleteDialogOpen}
				cancelAction={() => setDeleteDialogOpen(false)}
				confirmAction={handleDelete}
				title="Warning"
			>
				Are you sure you want to continue and delete this role?
			</ConfirmDialog>
			<ConfirmDialog
				isOpen={isDuplicateDialogOpen}
				cancelAction={() => setDuplicateDialogOpen(false)}
				confirmAction={handleDuplicate}
				title="Duplicate Role"
			>
				Are you sure you want to continue and duplicate this role?
			</ConfirmDialog>
		</>
	);
}
